import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@auth/AuthContext";
import Forbidden from "../../pages/errors/Forbidden";

/**
 * Permission-aware route guard. Wrap protected routes inside `routes.jsx`:
 *
 *   <AuthGuard>
 *     <PermissionGuard permission="kb.view">
 *       <KnowledgeBasePage />
 *     </PermissionGuard>
 *   </AuthGuard>
 *
 * Props (mutually exclusive):
 *   - `permission` (string) — required permission key.
 *   - `anyOf` (string[]) — caller must hold any one of these.
 *   - `allOf` (string[]) — caller must hold every one.
 *
 * Optional:
 *   - `fallback` — `"403"` (default), `"dashboard"`, or a React node.
 *
 * Behavior:
 *   - While AuthContext is loading, renders null (avoids the cold-load
 *     403 flash before /me resolves).
 *   - On miss, renders the chosen fallback. Default is the Forbidden page,
 *     which shows the missing key in `import.meta.env.DEV` only.
 *
 * Pairs with `<HasPermission>` for in-page button/section gating; the two
 * have different concerns and shouldn't be used interchangeably.
 */
function PermissionGuard({
  children,
  permission,
  anyOf,
  allOf,
  fallback = "403",
}) {
  const { permissions, loading } = useAuth();

  // Wait for /me to resolve before evaluating — otherwise every cold load
  // 403-flashes during the brief window between mount and hydration.
  if (loading) return null;

  const has = (key) => permissions?.includes(key);

  let allowed = true;
  let missing = null;

  if (permission) {
    allowed = has(permission);
    if (!allowed) missing = permission;
  } else if (Array.isArray(anyOf) && anyOf.length > 0) {
    allowed = anyOf.some(has);
    if (!allowed) missing = anyOf.join(" | ");
  } else if (Array.isArray(allOf) && allOf.length > 0) {
    const miss = allOf.find((k) => !has(k));
    allowed = !miss;
    if (!allowed) missing = miss;
  }

  if (allowed) return <>{children}</>;

  if (fallback === "dashboard") {
    return <Navigate to="/" replace />;
  }
  if (React.isValidElement(fallback)) {
    return fallback;
  }
  // Default 403
  return <Forbidden missingPermission={missing} />;
}

export default PermissionGuard;
